import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Divider, PopoverShell, SparkIcon, Text } from '@spark';

const FilterSection = ({ title, items, isChecked, onToggle, emptyText }) => (
  <div>
    <Text
      variant="detail-regular"
      style={{ color: 'var(--semantic-content-tertiary)', display: 'block', marginBottom: 8 }}
    >
      {title}
    </Text>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {(items || []).map((i) => (
        <Checkbox
          key={i.key}
          label={i.label}
          status={isChecked(i.key) ? 'active' : 'inactive'}
          onChange={(e) => onToggle(i.key, e.target.checked)}
        />
      ))}
      {(!items || items.length === 0) && (
        <Text variant="detail-regular" style={{ color: 'var(--semantic-content-tertiary)' }}>
          {emptyText}
        </Text>
      )}
    </div>
  </div>
);

/**
 * A small "apply/cancel" filter dropdown for showing/hiding metric columns.
 *
 * Sections:
 * - Classic
 * - LLM as a Judge
 * - Custom
 */
const SamplesFilterDropdown = ({
  classicItems,
  judgeItems,
  customItems,
  hiddenKeys,
  onChangeHiddenKeys,
  buttonLabel = 'Filter',
  buttonIcon,
}) => {
  const [open, setOpen] = useState(false);
  const [draftHidden, setDraftHidden] = useState(() => new Set(hiddenKeys || []));
  const wrapRef = useRef(null);

  // When the dropdown opens, start from the current committed state.
  useEffect(() => {
    if (open) setDraftHidden(new Set(hiddenKeys || []));
  }, [open, hiddenKeys]);

  // Close when clicking outside the trigger/panel.
  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  const allKeys = useMemo(() => {
    const keys = [];
    (classicItems || []).forEach((i) => i?.key && keys.push(i.key));
    (judgeItems || []).forEach((i) => i?.key && keys.push(i.key));
    (customItems || []).forEach((i) => i?.key && keys.push(i.key));
    return keys;
  }, [classicItems, judgeItems, customItems]);

  const isChecked = (k) => !draftHidden.has(k);

  const toggleKey = (k, checked) => {
    setDraftHidden((prev) => {
      const next = new Set(prev);
      if (checked) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const reset = () => setDraftHidden(new Set());
  const apply = () => {
    onChangeHiddenKeys?.(Array.from(draftHidden));
    setOpen(false);
  };

  const hasAny = allKeys.length > 0;
  const isResetDisabled = !hasAny || draftHidden.size === 0;

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'inline-block' }}>
      <Button
        className="fs-unmask"
        variant="secondary"
        size="small"
        showStartIcon
        startIcon={buttonIcon ?? <SparkIcon slug="filter" size="small" />}
        onClick={() => setOpen((o) => !o)}
      >
        {buttonLabel}
      </Button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: `calc(100% + var(--dimension-spacing-04))`,
            right: 0,
            zIndex: 1000,
          }}
        >
          <PopoverShell tip={false}>
            <div style={{ width: 280, padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <FilterSection
                title="Classic"
                items={classicItems}
                isChecked={isChecked}
                onToggle={toggleKey}
                emptyText="No classic metrics"
              />
              <Divider />
              <FilterSection
                title="LLM as a Judge"
                items={judgeItems}
                isChecked={isChecked}
                onToggle={toggleKey}
                emptyText="No judge criteria"
              />
              <Divider />
              <FilterSection
                title="Custom"
                items={customItems}
                isChecked={isChecked}
                onToggle={toggleKey}
                emptyText="No custom fields"
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button variant="ghost" size="small" disabled={isResetDisabled} onClick={reset}>
                  Reset
                </Button>
                <Button variant="primary" size="small" disabled={!hasAny} onClick={apply}>
                  OK
                </Button>
              </div>
            </div>
          </PopoverShell>
        </div>
      )}
    </div>
  );
};

export default SamplesFilterDropdown;
